import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
  frontendActionListToolDefinition,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { Parser } from '@cotera/era';
import { CocoChatModel } from '../models/coco-chat.model';
import { ResourceType } from '@cotera/api';
import { runChatSeedPreSubmitForSubmitParams } from '../../chat-seed/pre-submit';
import type { ChatSeedPreSubmitClients } from '../../chat-seed/types';
import { withoutSeedIsNewFlag } from '../../chat-seed/types';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { createIdGenerator, type UIMessage, type FileUIPart } from 'ai';
import {
  prepareChatUploads,
  type ChatUploadPart,
} from '../upload-chat-dataset';
import { CONVERSATION_SYSTEM_MESSAGE_ID, Conversation } from '@cotera/sdk/core';
import {
  getMessageLengthFromPayload,
  getMessageTextFromPayload,
  hasContentToSubmit,
  hasOnlyInitialContext,
} from '../utils/prompt-utils';
import { messageTriggersPlanMode } from '../utils/plan-mode-keywords';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

const generateMessageId = createIdGenerator({ prefix: 'msg', size: 16 });

const addArtifactParamsSchema = z.object({
  artifactType: z.string(),
  artifactId: z.string().optional(),
  data: z.unknown(),
});

const initialActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('focus_agent'), agentId: z.string().uuid() }),
  z.object({ type: z.literal('unfocus_agent') }),
  z.object({
    type: z.literal('output_shape'),
    shape: Parser.ExtendedAttributeTypeSchema,
  }),
  z.object({ type: z.literal('auto_approve') }),
  z.object({ type: z.literal('remove_auto_approve') }),
]);

const submitCocoChatInputSchema = z.object({
  text: z.string().optional(),
  editorState: z
    .object({
      type: z.literal('doc'),
      content: z.array(z.unknown()).optional(),
    })
    .optional(),
  files: z.array(z.record(z.string(), z.unknown())).optional(),
  systemSuffix: z.string().optional(),
  messageType: z.enum(['chat', 'hidden']).optional(),
  initialArtifacts: z.array(addArtifactParamsSchema).optional(),
  initialToolKeys: z.array(z.string()).optional(),
  initialActions: z.array(initialActionSchema).optional(),
  agentId: z.string().uuid().optional(),
});

type Params = z.infer<typeof submitCocoChatInputSchema>;

export class SubmitCocoChatAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Submit Coco Chat Message';
  inputSchema = submitCocoChatInputSchema;
  group = 'Chat';

  constructor(
    private readonly client: TenantedClient['resource']['chat'],
    private readonly seedPreSubmitClients: ChatSeedPreSubmitClients,
    private readonly orgId: string,
    private readonly userId: string,
    private readonly queryClient: Pick<QueryClient, 'invalidateQueries'>
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const mergedParams = model.mergeDeferredSeedIntoSubmitParams(params);

    if (!hasContentToSubmit(mergedParams)) {
      return ok({});
    }

    // Auto-trigger plan mode when the message mentions planning keywords. This
    // lives here (not in the chat UI's submit handler) so it also covers the
    // first message of a new chat, which is submitted via the pending-submission
    // path rather than the input box. Sticky: only flip it on, never off.
    // Persistence is handled uniformly below via context.planMode — the server
    // reconciles the conversation to it for both new and existing chats.
    if (
      !model.planModeWatchable.snapshot() &&
      messageTriggersPlanMode(getMessageTextFromPayload(mergedParams))
    ) {
      model.planModeWatchable.set(true);
    }

    const preSubmitResult = await runChatSeedPreSubmitForSubmitParams(
      mergedParams,
      {
        folderId: model.folderId,
        clients: this.seedPreSubmitClients,
      }
    );
    if (preSubmitResult !== null && !preSubmitResult.ok) {
      return err([{ t: 'error', message: preSubmitResult.message }]);
    }

    const previousInput = model.inputWatchable.snapshot();
    model.setInputValue('');

    // Tabular attachments become chat-scoped datasets rather than inlined file
    // parts: the rows never enter the transcript, and the server turn posts an
    // in-progress upload placeholder that completes into a dataset card. Only
    // the non-tabular attachments stay inline. Runs before the optimistic send
    // so an upload failure aborts cleanly instead of half-sending.
    const rawFileParts = (mergedParams.files ?? []) as ChatUploadPart[];
    let fileParts: FileUIPart[];
    try {
      // On the new-chat path these tabular files were already uploaded before
      // navigation (they arrive as receipts and are skipped here); on the
      // existing-chat path the real File is still in memory and is uploaded now.
      const prepared = await prepareChatUploads(
        this.seedPreSubmitClients.dataset,
        model.conversationId,
        rawFileParts
      );
      fileParts = prepared.inlineParts;
    } catch (uploadError) {
      model.statusWatchable.set('error');
      if (mergedParams.editorState !== undefined) {
        model.setInputEditorState(mergedParams.editorState);
      } else {
        model.setInputValue(mergedParams.text ?? previousInput);
      }
      return err([
        {
          t: 'error',
          message:
            uploadError instanceof Error
              ? uploadError.message
              : 'Failed to upload attachment',
        },
      ]);
    }
    let userMessage: UIMessage;
    if (mergedParams.editorState !== undefined) {
      userMessage = Conversation.createRichTextUserMessage(
        mergedParams.editorState as Parameters<
          typeof Conversation.createRichTextUserMessage
        >[0],
        generateMessageId()
      );
      if (fileParts.length > 0) {
        userMessage = {
          ...userMessage,
          parts: [...fileParts, ...(userMessage.parts ?? [])],
        };
      }
    } else {
      const text = mergedParams.text?.trim() ?? '';
      userMessage = {
        id: generateMessageId(),
        role: 'user',
        parts: [
          ...fileParts,
          ...(text.length > 0
            ? [{ type: 'text' as const, text }]
            : [{ type: 'text' as const, text: ' ' }]),
        ],
      };
    }

    model.statusWatchable.set('submitted');
    model.cancelledWatchable.set(false);
    model.setOptimisticallySentMessage(userMessage);

    // Always include the message we're submitting. When conversation is null (e.g. new
    // chat before first load), model.messages is [] and mergeOptimisticSentMessage
    // returns null—without this we'd send no messages and the LLM would respond to
    // an empty context (e.g. with an intro), making it look like an empty message was injected.
    // Omit persisted system rows (branch stores them as hidden stored-system-message parts
    // without `parts`); the server already has them and the chat contract requires parts.
    const existingMessages = model.messages
      .filter(
        (m) =>
          m.id !== CONVERSATION_SYSTEM_MESSAGE_ID &&
          m.role !== 'system' &&
          typeof m.role === 'string' &&
          typeof m.id === 'string' &&
          (m.parts ?? []).length > 0
      )
      .map((m) => ({
        id: m.id,
        role: m.role,
        parts: m.parts ?? [],
      }));
    const userMessageType =
      mergedParams.messageType !== undefined
        ? mergedParams.messageType
        : hasOnlyInitialContext(mergedParams)
        ? ('hidden' as const)
        : undefined;

    const messageAlreadyIncluded = existingMessages.some(
      (m) => m.id === userMessage.id
    );
    const messagesToSend = messageAlreadyIncluded
      ? existingMessages.map((message) =>
          message.id === userMessage.id && userMessageType !== undefined
            ? { ...message, messageType: userMessageType }
            : message
        )
      : [
          ...existingMessages,
          {
            id: userMessage.id,
            role: userMessage.role,
            parts: userMessage.parts,
            ...(userMessageType !== undefined && {
              messageType: userMessageType,
            }),
          },
        ];

    let result;
    try {
      result = await this.client.chat({
        context: {
          orgId: this.orgId,
          toolIds: [],
          agent: null,
          // Omitted when the user made no explicit choice so the server keeps
          // the conversation's persisted model (see CocoChatModel.explicitModel).
          ...(model.explicitModel !== undefined && {
            model: model.explicitModel,
          }),
          conversationId: model.conversationId,
          mode: model.autoApproveToolsWatchable.snapshot() ? 'auto' : 'manual',
          // Always sent so the server reconciles plan mode for new + existing chats.
          planMode: model.planModeWatchable.snapshot(),
          systemSuffix: mergedParams.systemSuffix,
          ...(model.folderId !== undefined && { folderId: model.folderId }),
          ...(mergedParams.agentId !== undefined && {
            agentId: mergedParams.agentId,
          }),
        },
        messages: messagesToSend,
        // Pre-submit has created any seeded resource by now, so the seed's
        // `isNew` marker must not be persisted with the artifact.
        initialArtifacts: withoutSeedIsNewFlag(mergedParams.initialArtifacts),
        initialToolKeys: mergedParams.initialToolKeys,
        initialActions: mergedParams.initialActions,
        // Sent every turn so the model can always ask what is on screen. Only
        // this one tool: what it lists is declared separately, at the point it
        // is listed, because by then the user may be somewhere else entirely.
        // Registration is hash-deduped server-side, so re-sending costs nothing.
        interruptableTools: [frontendActionListToolDefinition()],
      });
    } catch (e) {
      model.statusWatchable.set('error');
      if (mergedParams.editorState !== undefined) {
        model.setInputEditorState(mergedParams.editorState);
      } else {
        model.setInputValue(mergedParams.text ?? previousInput);
      }
      model.clearOptimisticallySentMessage();
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to send message';
      model.errorWatchable.set(new Error(errorMessage));
      return err([{ t: 'error', message: errorMessage }]);
    }

    if (result.isErr()) {
      model.statusWatchable.set('error');
      if (mergedParams.editorState !== undefined) {
        model.setInputEditorState(mergedParams.editorState);
      } else {
        model.setInputValue(mergedParams.text ?? previousInput);
      }
      model.clearOptimisticallySentMessage();
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to send message';
      model.errorWatchable.set(new Error(errorMessage));
      return err([{ t: 'error', message: errorMessage }]);
    }

    // Do not clear optimistic message here. The ConversationEventWatchable may not
    // have received the broadcast yet; clearing would cause a brief flicker. Let
    // mergeOptimisticSentMessage handle deduplication once the server event arrives.
    void model.chatStatusWatchable.refresh();

    // Fallback: if status stays 'submitted' (e.g. run completes so fast that derived
    // never changes, so MixedSourceWatchable never clears the override), force to ready.
    setTimeout(() => {
      if (model.statusWatchable.snapshot() === 'submitted') {
        model.statusWatchable.set('ready');
      }
    }, 2000);

    this.invalidateConversations();

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    const messageLength = getMessageLengthFromPayload(payload);
    const hasEditorState =
      payload.editorState !== undefined &&
      (payload.editorState.content?.length ?? 0) > 0;
    const fileCount = payload.files?.length ?? 0;

    return cocoActionMetadata(CocoEvent.ChatMessageSubmitted, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
      messageLength,
      hasContent: messageLength > 0 || hasEditorState || fileCount > 0,
      fileCount,
      model: model.model,
      autoApproveTools: model.autoApproveToolsWatchable.snapshot(),
    });
  }

  private invalidateConversations(): void {
    const queryKey = queryKeys.resource.list({
      orgId: this.orgId,
      type: ResourceType.Chat,
      limit: 50,
    });

    void this.queryClient.invalidateQueries({ queryKey });
  }
}
